import type { ComponentType, ReactElement } from 'react';
import { Check, Pin, PinOff, SlidersHorizontal } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { cn } from '@reflex/ui/lib/utils';
import { useHumanReadableCommands, useTurnMenuVisible } from '@reflex/ui/client/prefs';

export interface HeaderDisplayItem {
  key: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  iconClassName?: string;
}

interface HeaderDisplayMenuProps {
  /** Tab items for the side-rail visibility section. */
  tabItems: HeaderDisplayItem[];
  tabHiddenSet: Set<string>;
  onToggleTab: (value: string) => void;
  onResetTabs?: () => void;
  /** Agent actions that can be pinned to the rail (or left completely hidden). */
  actionItems?: HeaderDisplayItem[];
  actionPinnedSet?: Set<string>;
  onToggleActionPinned?: (key: string) => void;
  className?: string;
  /** Where the menu opens relative to the trigger. Default `left` (rail). */
  menuSide?: 'top' | 'right' | 'bottom' | 'left';
  /** Replace the default toolbar-icon trigger (used by the mobile tool tiles). */
  trigger?: ReactElement;
  /**
   * `nested` (default) uses sideways submenus — fine beside the desktop rail.
   * `flat` inlines Views / Actions / Preferences in one scrollable panel so
   * nothing flies off-screen on a phone.
   */
  layout?: 'nested' | 'flat';
}

function TabPinItems({
  tabItems,
  tabHiddenSet,
  onToggleTab,
  onResetTabs,
  dense,
}: {
  tabItems: HeaderDisplayItem[];
  tabHiddenSet: Set<string>;
  onToggleTab: (value: string) => void;
  onResetTabs?: () => void;
  dense?: boolean;
}) {
  return (
    <>
      {tabItems.map(({ key, label, icon: Icon }) => {
        const hidden = tabHiddenSet.has(key);
        const pinnedCount = tabItems.length - tabHiddenSet.size;
        const isLastPinned = !hidden && pinnedCount <= 1;
        return (
          <DropdownMenuItem
            key={key}
            disabled={isLastPinned}
            className={dense ? 'min-h-11' : undefined}
            aria-label={
              isLastPinned ? `Keep ${label} pinned` : hidden ? `Pin ${label}` : `Unpin ${label}`
            }
            data-testid={`header-display-tab-${key}`}
            onSelect={(event) => {
              event.preventDefault();
              if (isLastPinned) return;
              onToggleTab(key);
            }}
          >
            {Icon ? <Icon className={cn('mr-2 h-4 w-4', hidden && 'opacity-40')} /> : null}
            <span className={cn(hidden && 'text-muted-foreground')}>{label}</span>
            <span className={cn('ml-auto text-muted-foreground/70', !hidden && 'text-foreground')}>
              {hidden ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
            </span>
          </DropdownMenuItem>
        );
      })}
      {onResetTabs ? (
        <div className="flex items-center justify-between gap-2 px-2 pb-1 pt-1.5">
          <p className="text-2xs leading-none text-muted-foreground">
            {dense
              ? 'Pin the views you want in the strip'
              : 'Drag tabs or actions on the rail to reorder'}
          </p>
          <button
            type="button"
            onClick={onResetTabs}
            className="rounded text-2xs leading-none text-muted-foreground underline-offset-2 outline-none hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            Reset
          </button>
        </div>
      ) : null}
    </>
  );
}

function ActionPinItems({
  actionItems,
  actionPinnedSet,
  onToggleActionPinned,
  dense,
}: {
  actionItems: HeaderDisplayItem[];
  actionPinnedSet: Set<string>;
  onToggleActionPinned: (key: string) => void;
  dense?: boolean;
}) {
  return (
    <>
      {actionItems.map(({ key, label, icon: Icon, iconClassName }) => {
        const pinned = actionPinnedSet.has(key);
        return (
          <DropdownMenuItem
            key={key}
            className={dense ? 'min-h-11' : undefined}
            aria-label={pinned ? `Unpin ${label}` : `Pin ${label}`}
            data-testid={`header-display-action-${key}`}
            onSelect={(event) => {
              event.preventDefault();
              onToggleActionPinned(key);
            }}
          >
            {Icon ? (
              <Icon
                className={cn('mr-2 h-4 w-4 shrink-0', iconClassName, !pinned && 'opacity-40')}
              />
            ) : null}
            <span className={cn('min-w-0 flex-1 truncate', !pinned && 'text-muted-foreground')}>
              {label}
            </span>
            <span
              className={cn('ml-auto text-muted-foreground/70', pinned && 'text-foreground')}
              data-testid={`header-display-pin-${key}`}
            >
              {pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
            </span>
          </DropdownMenuItem>
        );
      })}
    </>
  );
}

/**
 * Display & view options for the agent rail: which side-rail tabs are
 * visible, which actions are pinned to the rail, plus transcript preferences.
 */
export function HeaderDisplayMenu({
  tabItems,
  tabHiddenSet,
  onToggleTab,
  onResetTabs,
  actionItems = [],
  actionPinnedSet = new Set(),
  onToggleActionPinned,
  className,
  menuSide = 'left',
  trigger,
  layout = 'nested',
}: HeaderDisplayMenuProps) {
  const [humanReadable, setHumanReadable] = useHumanReadableCommands();
  const [turnMenu, setTurnMenu] = useTurnMenuVisible();

  const modified = tabHiddenSet.size > 0 || !humanReadable || !turnMenu;
  const flat = layout === 'flat';

  const defaultTrigger = (
    <Button
      variant="ghost"
      size="toolbar-icon"
      className={cn('text-muted-foreground', modified && 'text-foreground', className)}
      aria-label="Display"
      data-testid="header-display-menu"
    >
      <SlidersHorizontal className="size-4" />
    </Button>
  );

  const preferenceItems = (
    <>
      <DropdownMenuItem
        className={flat ? 'min-h-11' : undefined}
        onSelect={(event) => {
          event.preventDefault();
          setHumanReadable(!humanReadable);
        }}
        data-testid="agent-view-human-readable"
      >
        <span className="mr-2 flex h-4 w-4 items-center justify-center">
          {humanReadable ? <Check className="h-4 w-4" /> : null}
        </span>
        Human readable commands
      </DropdownMenuItem>
      <DropdownMenuItem
        className={flat ? 'min-h-11' : undefined}
        onSelect={(event) => {
          event.preventDefault();
          setTurnMenu(!turnMenu);
        }}
        data-testid="agent-view-turn-menu"
      >
        <span className="mr-2 flex h-4 w-4 items-center justify-center">
          {turnMenu ? <Check className="h-4 w-4" /> : null}
        </span>
        Turn menu
      </DropdownMenuItem>
    </>
  );

  return (
    <DropdownMenu>
      {trigger ? (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{defaultTrigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side={menuSide === 'left' ? 'left' : 'top'}>Display</TooltipContent>
        </Tooltip>
      )}
      <DropdownMenuContent
        side={menuSide}
        align="end"
        collisionPadding={12}
        className={cn(
          flat
            ? 'max-h-[min(70vh,28rem)] w-[min(20rem,calc(100vw-1.5rem))] overflow-y-auto'
            : 'w-56',
        )}
      >
        {flat ? (
          <>
            <DropdownMenuLabel>Views</DropdownMenuLabel>
            <TabPinItems
              tabItems={tabItems}
              tabHiddenSet={tabHiddenSet}
              onToggleTab={onToggleTab}
              onResetTabs={onResetTabs}
              dense
            />
            {actionItems.length > 0 && onToggleActionPinned ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <ActionPinItems
                  actionItems={actionItems}
                  actionPinnedSet={actionPinnedSet}
                  onToggleActionPinned={onToggleActionPinned}
                  dense
                />
              </>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Preferences</DropdownMenuLabel>
            {preferenceItems}
          </>
        ) : (
          <>
            {/* Parent + submenus use align=end so the rail-bottom trigger opens
                upward and nested panels grow from their trigger row, not down.
                (SubContent align=end is emulated in @reflex/ui — Radix hardcodes start.) */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="header-display-tabs-sub">
                Side-rail tabs
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent align="end" className="w-64">
                <TabPinItems
                  tabItems={tabItems}
                  tabHiddenSet={tabHiddenSet}
                  onToggleTab={onToggleTab}
                  onResetTabs={onResetTabs}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {actionItems.length > 0 && onToggleActionPinned ? (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid="header-display-actions-sub">
                  Actions
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent align="end" className="w-64">
                  <ActionPinItems
                    actionItems={actionItems}
                    actionPinnedSet={actionPinnedSet}
                    onToggleActionPinned={onToggleActionPinned}
                  />
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ) : null}

            <DropdownMenuSeparator />
            <DropdownMenuLabel>Preferences</DropdownMenuLabel>
            {preferenceItems}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
